import React, { useState } from 'react'
import { useQuery } from '@apollo/react-hooks'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  History,
  Search,
  Folder,
  FileText,
  Upload,
  Download,
  Pencil,
  Trash2,
  Share2,
  Eye,
  Send,
  User,
  Lock,
  LoaderCircle,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
} from 'lucide-react'
import useRouter from '../../../hooks/useRouter'
import * as Query from '../../../api/gql/query'
import { formatDateTime, formatRelativeTime } from '../../../utils/formatters'
import { cn } from '@/lib/utils'
import { docActivityVariant, type StatusVariant } from '@/lib/status-variants'

interface ActivityLogEntry {
  id: string
  action: string
  folderId: string | null
  documentId: string | null
  revisionId: string | null
  transmittalId: string | null
  shareLinkId: string | null
  metadata: any
  ipAddress: string | null
  createdAt: string
  user: {
    id: string
    name: string
  } | null
}

type ActionCategory = 'ALL' | 'FOLDER' | 'DOCUMENT' | 'SHARE' | 'PERMISSION' | 'TRANSMITTAL'

const getActionIcon = (action: string) => {
  switch (action) {
    case 'FOLDER_CREATE':
    case 'FOLDER_UPDATE':
    case 'FOLDER_DELETE':
    case 'FOLDER_MOVE':
      return Folder
    case 'DOCUMENT_UPLOAD':
    case 'DOCUMENT_RESTORE':
      return Upload
    case 'DOCUMENT_DELETE':
    case 'DOCUMENT_BULK_DELETE':
      return Trash2
    case 'DOCUMENT_RENAME':
      return Pencil
    case 'DOCUMENT_MOVE':
      return FileText
    case 'DOCUMENT_DOWNLOAD':
      return Download
    case 'DOCUMENT_VIEW':
    case 'DOCUMENT_PREVIEW':
      return Eye
    case 'SHARE_LINK_CREATE':
    case 'SHARE_LINK_ACCESS':
    case 'SHARE_LINK_REVOKE':
      return Share2
    case 'PERMISSION_GRANT':
    case 'PERMISSION_REVOKE':
    case 'PERMISSION_UPDATE':
      return Lock
    case 'TRANSMITTAL_CREATE':
    case 'TRANSMITTAL_SEND':
    case 'TRANSMITTAL_VIEW':
    case 'TRANSMITTAL_DELETE':
      return Send
    default:
      return History
  }
}

/** Icon-disc tint per badge variant — the solid counterpart of the badge's tint. */
const ICON_CLASSES: Record<string, string> = {
  success: 'bg-green-100 text-green-600',
  danger: 'bg-red-100 text-red-600',
  warning: 'bg-amber-100 text-amber-600',
  info: 'bg-blue-100 text-blue-600',
}

/**
 * SEND has no status variant — it is drawn in the brand tint to read as "outbound",
 * which is a different axis from the create/delete/update/view signals.
 */
const getActionStyle = (action: string): { icon: string; variant: StatusVariant; badge?: string } => {
  const variant = docActivityVariant(action)
  if (variant !== 'outline') return { icon: ICON_CLASSES[variant], variant }
  if (action.includes('SEND'))
    return { icon: 'bg-primary/10 text-primary', variant, badge: 'border-primary/20 bg-primary/5 text-primary' }
  return { icon: 'bg-muted text-muted-foreground', variant }
}

const ActivityLog: React.FC = () => {
  const { t } = useTranslation()
  const { match, history } = useRouter()
  const PAGE_SIZE = 25
  const [searchQuery, setSearchQuery] = useState('')
  const [actionFilter, setActionFilter] = useState<ActionCategory>('ALL')
  const [currentPage, setCurrentPage] = useState(1)

  // Project from the route (the page being displayed), not me.currentProject —
  // they diverge when navigating by URL and the tab then showed the wrong
  // (usually empty) project's activity (BUG-DC-04). ME stays as a fallback
  // for mounts without a :projectId param.
  const { data: meData } = useQuery(Query.ME)
  const projectId = match?.params?.projectId || meData?.me?.currentProject?.id

  const navigateTo = (url: string) => (e: React.MouseEvent) => {
    e.preventDefault()
    history.push(url)
  }

  const docLink = (log: ActivityLogEntry, label: string) => {
    if (!log.documentId || !log.folderId) return <span className="font-semibold">{label}</span>
    const url = `${match.url}?tab=documents&folderId=${log.folderId}&documentId=${log.documentId}`
    return <a href="#" className="font-semibold text-primary hover:underline" onClick={navigateTo(url)}>{label}</a>
  }

  const folderLink = (folderId: string | null, label: string) => {
    if (!folderId) return <span className="font-semibold">{label}</span>
    const url = `${match.url}?tab=documents&folderId=${folderId}`
    return <a href="#" className="font-semibold text-primary hover:underline" onClick={navigateTo(url)}>{label}</a>
  }

  const transmittalLink = (log: ActivityLogEntry, label: string) => {
    if (!log.transmittalId) return <span className="font-semibold">{label}</span>
    const url = `${match.url}?tab=transmittals&transmittalId=${log.transmittalId}`
    return <a href="#" className="font-semibold text-primary hover:underline" onClick={navigateTo(url)}>{label}</a>
  }

  // Fetch a large batch and paginate locally (API doesn't return totalCount)
  const { data, loading } = useQuery(Query.DOC_ACTIVITY_LOGS, {
    variables: {
      filter: {
        projectId: projectId || undefined,
      },
      limit: 500,
      offset: 0,
    },
    skip: !projectId,
    fetchPolicy: 'cache-and-network',
  })

  const logs: ActivityLogEntry[] = data?.docActivityLogs || []

  // Filter locally by search query and action type
  const filteredLogs = logs.filter((log) => {
    if (actionFilter !== 'ALL' && !log.action.includes(actionFilter)) return false
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    const userName = log.user?.name?.toLowerCase() || ''
    const metadata = JSON.stringify(log.metadata || {}).toLowerCase()
    return userName.includes(query) || metadata.includes(query) || log.action.toLowerCase().includes(query)
  })

  const totalPages = Math.ceil(filteredLogs.length / PAGE_SIZE)
  const paginatedLogs = filteredLogs.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery, actionFilter])

  const formatAction = (action: string): string => {
    return action
      .split('_')
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ')
  }

  const renderLogDescription = (log: ActivityLogEntry): React.ReactNode | null => {
    const m = log.metadata || {}

    switch (log.action) {
      case 'DOCUMENT_VIEW':
      case 'DOCUMENT_PREVIEW':
      case 'DOCUMENT_DOWNLOAD':
        return m.name ? <>{docLink(log, m.name)}</> : null
      case 'DOCUMENT_UPLOAD':
        return m.name
          ? <>{docLink(log, m.name)} <span className="text-muted-foreground">({m.filename}, v{m.version})</span></>
          : m.filename ? <>{m.filename} <span className="text-muted-foreground">(v{m.version})</span></> : null
      case 'DOCUMENT_RENAME':
        return m.oldName && m.newName
          ? <>{m.oldName} <ArrowRight className="inline h-3 w-3 mx-1" /> {docLink(log, m.newName)}</>
          : null
      case 'DOCUMENT_DELETE':
        return m.name ? <>{m.name}</> : null
      case 'DOCUMENT_BULK_DELETE':
        return m.count ? <>{m.count} {t('DocControl_documents').toLowerCase()}</> : null
      case 'DOCUMENT_MOVE':
        return m.name
          ? <>{docLink(log, m.name)}{m.newFolderName && <span className="text-muted-foreground"> <ArrowRight className="inline h-3 w-3 mx-1" /> {folderLink(m.newFolderId || log.folderId, m.newFolderName)}</span>}</>
          : null
      case 'DOCUMENT_RESTORE':
        return m.name
          ? <>{docLink(log, m.name)} <span className="text-muted-foreground">(v{m.restoredVersion})</span></>
          : null

      case 'FOLDER_CREATE':
        return m.name ? <>{folderLink(log.folderId, m.name)}</> : null
      case 'FOLDER_DELETE':
        return m.name ? <>{m.name}</> : null
      case 'FOLDER_UPDATE':
        return m.oldName && m.newName && m.oldName !== m.newName
          ? <>{m.oldName} <ArrowRight className="inline h-3 w-3 mx-1" /> {folderLink(log.folderId, m.newName)}</>
          : m.name ? <>{folderLink(log.folderId, m.name)}</> : null
      case 'FOLDER_MOVE':
        return m.name
          ? <>{folderLink(log.folderId, m.name)}{m.parentName && <span className="text-muted-foreground"> <ArrowRight className="inline h-3 w-3 mx-1" /> {m.parentName}</span>}</>
          : null

      case 'PERMISSION_GRANT':
        return <>{m.role} {t('DocControl_log_to')} <span className="font-semibold">{m.targetUserName || m.projectRole}</span>{m.folderName && <> {t('DocControl_log_on')} {folderLink(log.folderId, m.folderName)}</>}</>
      case 'PERMISSION_REVOKE':
        return <><span className="font-semibold">{m.targetUserName || m.projectRole}</span>{m.folderName && <> {t('DocControl_log_from')} {folderLink(log.folderId, m.folderName)}</>}</>
      case 'PERMISSION_UPDATE':
        return <><span className="font-semibold">{m.targetUserName || m.projectRole}</span> <ArrowRight className="inline h-3 w-3 mx-1" /> {m.role}{m.folderName && <> {t('DocControl_log_on')} {folderLink(log.folderId, m.folderName)}</>}</>

      case 'SHARE_LINK_CREATE':
      case 'SHARE_LINK_ACCESS':
      case 'SHARE_LINK_REVOKE':
        return m.targetName
          ? <>{log.documentId ? docLink(log, m.targetName) : folderLink(log.folderId, m.targetName)}</>
          : null

      case 'TRANSMITTAL_CREATE':
        return m.name ? <>{transmittalLink(log, m.name)}{m.reference && <span className="text-muted-foreground"> ({m.reference})</span>}</> : null
      case 'TRANSMITTAL_SEND':
        return m.name
          ? <>{transmittalLink(log, m.name)} <span className="text-muted-foreground">({m.recipientCount} {t('DocControl_recipients').toLowerCase()}, {m.itemCount} {t('DocControl_documents').toLowerCase()})</span></>
          : null
      case 'TRANSMITTAL_VIEW':
        return m.name ? <>{transmittalLink(log, m.name)}</> : null
      case 'TRANSMITTAL_DELETE':
        return m.name ? <>{m.name}{m.reference && <span className="text-muted-foreground"> ({m.reference})</span>}</> : null

      default:
        const fallback = m.name || m.documentName || m.folderName || m.transmittalName
        return fallback ? <>{fallback}</> : null
    }
  }

  return (
    <div className="rounded-lg border bg-card">
      {/* Toolbar */}
      <div className="flex items-center gap-3 p-3 border-b flex-wrap">
        <div className="flex items-center gap-1.5">
          <span className="text-lg font-bold">{filteredLogs.length}</span>
          <span className="text-sm text-muted-foreground">{t('DocControl_activity_log')}</span>
        </div>

        <div className="flex-1" />

        {/* Action Filter */}
        <Select value={actionFilter} onValueChange={(v) => setActionFilter(v as ActionCategory)}>
          <SelectTrigger className="h-8 w-auto min-w-[160px] text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">{t('DocControl_all_actions')}</SelectItem>
            <SelectItem value="FOLDER">{t('DocControl_folder_actions')}</SelectItem>
            <SelectItem value="DOCUMENT">{t('DocControl_document_actions')}</SelectItem>
            <SelectItem value="SHARE">{t('DocControl_share_actions')}</SelectItem>
            <SelectItem value="PERMISSION">{t('DocControl_permission_actions')}</SelectItem>
            <SelectItem value="TRANSMITTAL">{t('DocControl_transmittal_actions')}</SelectItem>
          </SelectContent>
        </Select>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('Common_search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8 w-[200px] pl-8 text-sm"
          />
        </div>
      </div>

      {/* Content */}
      {loading && !data ? (
        <div className="flex items-center justify-center p-12 text-muted-foreground">
          <LoaderCircle className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : paginatedLogs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
          <History className="h-10 w-10 mb-3 opacity-40" />
          <p className="text-sm">{t('DocControl_no_activity')}</p>
        </div>
      ) : (
        <>
          <div className="divide-y">
            {paginatedLogs.map((log) => {
              const Icon = getActionIcon(log.action)
              const style = getActionStyle(log.action)

              return (
                <div key={log.id} className="flex items-start gap-3 px-4 py-3 hover:bg-muted/50 transition-colors">
                  {/* Icon */}
                  <div className={cn('flex items-center justify-center rounded-full h-9 w-9 shrink-0', style.icon)}>
                    <Icon className="h-4 w-4" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 flex-wrap min-w-0">
                        <Badge variant={style.variant} className={cn('text-xs font-medium whitespace-nowrap', style.badge)}>
                          {t(`DocControl_action_${log.action.toLowerCase()}`, formatAction(log.action))}
                        </Badge>
                        {renderLogDescription(log) && (
                          <span className="text-sm truncate">{renderLogDescription(log)}</span>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0" title={formatDateTime(log.createdAt)}>
                        {formatRelativeTime(log.createdAt)}
                      </span>
                    </div>

                    <div className="flex items-center gap-1.5 mt-1 text-xs text-muted-foreground">
                      <User className="h-3 w-3" />
                      <span>{log.user?.name || t('DocControl_system')}</span>
                      {log.ipAddress && (
                        <>
                          <span>·</span>
                          <span>{log.ipAddress}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <span className="text-xs text-muted-foreground">
                {t('Grid_showing_range', {
                  defaultValue: 'Showing {{from}}-{{to}} of {{total}}',
                  from: (currentPage - 1) * PAGE_SIZE + 1,
                  to: Math.min(currentPage * PAGE_SIZE, filteredLogs.length),
                  total: filteredLogs.length,
                })}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 w-7 p-0"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-xs text-muted-foreground min-w-[60px] text-center">
                  {currentPage} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 w-7 p-0"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default ActivityLog
